import { Flex, Modal, Typography, message } from 'antd';
import { CloseOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CustomButton } from '@/components/ui';
import { rejectReschedule } from '@/api/booking';

import { notifyActionError } from '@/helpers/subscriptionError';
interface RejectRescheduleProps {
  open: boolean;
  onCancel: () => void;
  bookingId: string;
  franchiseName?: string;
}

// TCX-852 (PO decision, no design mock exists for this one — follows the
// established modal chrome/copy conventions from KeepFee/LateCancelConfirm;
// revisit once design has a mock). Consultant confirmation for rejecting a
// franchisor's reschedule request outright: the meeting reverts to its
// original time and no fee is ever charged — this is not a "keep fee"
// decision, it's "I can't do either time, go message me directly."
export const RejectReschedule = (props: RejectRescheduleProps) => {
  const { bookingId } = props;
  const franchiseName = props.franchiseName ?? 'The franchisor';
  const queryClient = useQueryClient();

  const reject = useMutation({
    mutationKey: ['reject-reschedule', bookingId],
    mutationFn: () => rejectReschedule(bookingId),
    onSuccess: () => {
      message.success('Reschedule rejected. The meeting is back at its original time.');
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['booking'] });
      props.onCancel();
    },
    // TCX-900: see helpers/subscriptionError — retryable outage vs rejection.
    onError: (error: any) => notifyActionError(error),
  });

  return (
    <Modal
      open={props.open}
      onCancel={props.onCancel}
      width={640}
      centered
      footer={null}
      closeIcon={<CloseOutlined style={{ color: '#000' }} />}
      styles={{ content: { borderRadius: 20 }, body: { padding: '18px 18px' } }}
    >
      <Flex vertical align={"center"} gap={16}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: '#FCE7E7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CloseCircleOutlined style={{ fontSize: 24, color: '#DC2626' }} />
        </div>
        <Typography.Text style={{ fontSize: 22, fontWeight: 700, textAlign: 'center' }}>
          Reject this reschedule request?
        </Typography.Text>
        <Typography.Text type={"secondary"} style={{ textAlign: 'center' }}>
          The meeting moves back to its original time and no fee is charged.{' '}
          <Typography.Text strong>{franchiseName}</Typography.Text> will be notified and asked to
          message you directly to find a time that works.
        </Typography.Text>
      </Flex>
      <Flex vertical gap={12} style={{ marginTop: 24 }}>
        <CustomButton
          type={"primary"}
          text={"REJECT & REVERT TO ORIGINAL TIME"}
          loading={reject.isPending}
          onClick={() => reject.mutate()}
          style={{ backgroundColor: '#DC2626', borderColor: '#DC2626' }}
        />
        <CustomButton type={"default"} text={"BACK"} onClick={props.onCancel} />
      </Flex>
    </Modal>
  );
};
